import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { devWarn } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/safety/Dialogs";
import ProfileBasicsCard from "@/components/settings/ProfileBasicsCard";
import RecoveryDetailsCard from "@/components/settings/RecoveryDetailsCard";
import BlockedMembersSection from "@/components/settings/BlockedMembersSection";
import TestimonialSection from "@/components/settings/TestimonialSection";
import DeleteAccountSection from "@/components/settings/DeleteAccountSection";
import { toast } from "sonner";

const PROGRAMS = ["AA", "NA", "CMA", "SMART", "MULTIPLE", "OTHER"];

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [blocksError, setBlocksError] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState({
    username: "",
    first_name: "",
    bio: "",
    recovery_program: "",
    gender: "",
    gender_preference: "no_preference",
    language: "",
    city: "",
    state: "",
    country: "",
    profile_photo: "",
    email_notifications: true,
  });

  useEffect(() => {
    if (user) {
      setForm({
        username: user.username || "",
        first_name: user.first_name || "",
        bio: user.bio || "",
        recovery_program: user.recovery_program || "",
        gender: user.gender || "",
        gender_preference: user.gender_preference || "no_preference",
        language: user.language || "English",
        city: user.location?.city || "",
        state: user.location?.state || "",
        country: user.location?.country || "",
        profile_photo: user.profile_photo || "",
        email_notifications: user.email_notifications !== false,
      });
    }
  }, [user]);

  const loadBlocks = useCallback(async () => {
    setBlocksError(false);
    try {
      const { data } = await api.get("/blocks");
      setBlocks(data.blocks);
    } catch (err) {
      // Non-blocking for the rest of Settings, but never silent: the member sees
      // an inline retry and details stay in development logs only.
      devWarn("Settings.loadBlocks", err);
      setBlocksError(true);
    }
  }, []);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Please choose an image under 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, profile_photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/auth/me", {
        username: form.username,
        first_name: form.first_name || null,
        bio: form.bio || null,
        recovery_program: form.recovery_program || null,
        gender: form.gender || null,
        gender_preference: form.gender_preference,
        language: form.language,
        profile_photo: form.profile_photo || null,
        email_notifications: form.email_notifications,
        location: {
          city: form.city || null,
          state: form.state || null,
          country: form.country || null,
        },
      });
      await refresh();
      toast.success("Your profile was updated.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="font-display text-3xl font-bold text-[#1F2A2E]">Profile &amp; Settings</h1>
      <p className="mt-1.5 text-[#4B5A60]">
        You control what other members see. A display name is all that is required.
      </p>

      <form onSubmit={save} className="mt-6 space-y-5">
        <ProfileBasicsCard user={user} form={form} setForm={setForm} onPhoto={onPhoto} />

        <RecoveryDetailsCard form={form} setForm={setForm} programs={PROGRAMS} />

        <div className="sm-card p-6 bg-white">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={form.email_notifications}
              onChange={(e) => setForm({ ...form, email_notifications: e.target.checked })}
              className="mt-1 w-4 h-4 accent-[#1F6F78]"
            />
            <span className="text-sm text-[#1F2A2E]">
              Email me about requests and new messages
              <span className="block text-xs text-[#7A868B] mt-0.5">
                We never include the contents of private messages in emails.
              </span>
            </span>
          </label>
        </div>

        <Button
          type="submit"
          disabled={busy}
          data-testid="save-settings-button"
          className="w-full h-12 bg-[#1F6F78] hover:bg-[#185B62] text-white"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
        </Button>
      </form>

      <TestimonialSection />

      <BlockedMembersSection
        blocks={blocks}
        blocksError={blocksError}
        reloadBlocks={loadBlocks}
      />

      <DeleteAccountSection onRequestDelete={() => setDeleteOpen(true)} />

      <ConfirmDialog
        open={deleteOpen}
        danger
        title="Delete your account?"
        description="Your profile will be removed and your connections ended. If you have an active membership, cancel it first so you are not billed again."
        confirmLabel="Yes, delete it"
        onClose={() => setDeleteOpen(false)}
        onConfirm={async () => {
          try {
            await api.delete("/auth/me");
            logout();
            toast.success("Your account was deleted. Take care of yourself.");
            navigate("/");
          } catch (e) {
            toast.error(apiError(e));
          }
        }}
      />
    </div>
  );
}
